import { Injectable, signal, Signal, WritableSignal } from "@angular/core";
import { ReportRunner } from "../../services/report-runner";
import { inject } from "@angular/core";
import { ReportOutput, ReportRunResponse, ReportRunResult } from "../../models/run_request";
import { isTableDisplayItem, ReportDisplayItem } from "../../models/runnable_report";
import { SortDirection } from "@angular/material/sort";

@Injectable()
export class ReportRunState {

    private readonly reportRunner = inject(ReportRunner);

    private reportId: string;
    private model: any;
    private _options = signal<any>({});
    public options = this._options.asReadonly();
    private runFor: string;
    private display: ReportDisplayItem[];
    private readonly = signal(false);

    // This flag indicates that the report contains sources with disabled security context
    // and the user is not allowed to bypass (e.i. either admin or not published)
    securityContextPreserved = signal(false);


    private runner?: (reportId: string, model: any, options: any, runFor: string) => Promise<ReportRunResponse>;

    private resultsByOutput: { [key: string]: WritableSignal<ReportOutput | undefined> } = {};

    private createOutputSignals(display: ReportDisplayItem[]) {
        return display.reduce((acc, item) => {
            if ('output' in item && item.output) {
                acc[item.output] = signal<ReportOutput | undefined>(undefined);
            }
            return acc;
        }, {} as { [key: string]: WritableSignal<ReportOutput | undefined> });
    }

    private getOrCreateOutputResult(outputName: string) {
        if (!this.resultsByOutput[outputName]) {
            this.resultsByOutput[outputName] = signal<ReportOutput | undefined>(undefined);
        }
        return this.resultsByOutput[outputName];
    }

    initialize(reportId: string, display: ReportDisplayItem[], model: any, options: any, runFor: string,
        runner?: (reportId: string, model: any, options: any, runFor: string, extra?: any) => Promise<ReportRunResponse>
    ) {
        this.reportId = reportId;
        this.model = model;
        this._options.set(options);
        this.runFor = runFor;
        const showType = options.showType;
        this.display = display.filter(d => (d.showOn || ['full', 'embedded', 'widget']).includes(showType))

        // Apply forceOutputOptions to all displays
        const forcedOutputOptions = options.forceOutputOptions;
        if (forcedOutputOptions) {
            this.display.forEach(disp => {
                if (isTableDisplayItem(disp)) {
                    disp.options = {...disp.options, ...forcedOutputOptions};
                }
            })
        }

        this.resultsByOutput = this.createOutputSignals(this.display);
        this.securityContextPreserved.set(false);
        this.runner = runner;
        if (!this.runner) {
            this.runner = (reportId: string, model: any, options: any, runFor: string) => {
                return this.reportRunner.executeById(reportId, model, options, runFor);
            }
        }
    }

    initializeWithResult(reportId: string, display: ReportDisplayItem[], model: any, options: any, runFor: string, result: ReportRunResponse) {
        this.initialize(reportId, display, model, options, runFor);
        this.processResult(result);

        // Force readonly view
        this.readonly.set(true);
    }

    async run() {
        if (!this.reportId || !this.model || !this.options() || !this.runFor) {
            throw new Error('Missing required parameters');
        }
        const result = await this.runner!(this.reportId, this.model, this.options(), this.runFor);
        this.processResult(result);
        return result;
    }

    getDisplay() {
        return this.display;
    }

    setModel(model: any) {
        this.model = model;
    }

    setDisplay(display: ReportDisplayItem[]) {
        this.display = display;
    }

    processResult(result: ReportRunResponse) {
        Object.values(this.resultsByOutput).forEach(output => output.set(undefined));
        result.result.forEach(outputResult => {
            this.getOrCreateOutputResult(outputResult.name).set(outputResult);
        });
        this.securityContextPreserved.set(!!result.security_review_required && !!result.security_context_preserved);
    }

    getOutputResult<T extends ReportOutput = ReportRunResult>(outputName: string): Signal<T | undefined> {
        return this.getOrCreateOutputResult(outputName).asReadonly() as Signal<T | undefined>;
    }

    async generateCSV(outputName: string) {
        return this.reportRunner.generateCSV(this.reportId, outputName, this.model, this.options(), this.runFor);
    }

    async getTaskStatus(taskId: string) {
        return this.reportRunner.getTaskStatus(taskId);
    }

    async getResultUrl(taskId: string) {
        return this.reportRunner.getResultUrl(taskId);
    }

    async abortTask(taskId: string) {
        return this.reportRunner.abortTask(taskId);
    }


    async loadOutputResult(displayItem: ReportDisplayItem, pagination?: {size: number, start: number}, sort?: { field: string, direction: SortDirection}) {
        if (!isTableDisplayItem(displayItem)) {
            throw new Error('Display item is not a table');
        }
        const options = {
            ...this.options(),
            limitTo: [displayItem.id],
            output_options: {
                [displayItem.output]: {
                    size: pagination?.size || 20,
                    start: pagination?.start || 0,
                    sort: sort,
                }
            }
        }

        if (!this.runner) {
            throw new Error('Runner not initialized');
        }
        const result = await this.runner(this.reportId, this.model, options, this.runFor);
        if (result && result.result) {
            const outputResult = result.result.find(r => r.name === displayItem.output);
            if (outputResult) {
                this.getOrCreateOutputResult(displayItem.output).set(outputResult);
            }
        }
    }
}
